/**
 * React Query hooks for Reports API
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { get } from '@/lib/api-client';
import type {
  ReportSummaryResponse,
  ReportTimelineResponse,
  ReportTopActorsResponse,
  GroupByDimension,
} from '@/types/reports';

interface TimeRangeParams {
  startDate?: string;
  endDate?: string;
  relativeDays?: number;
}

/**
 * Hook to fetch report summary grouped by dimension
 */
export function useReportSummary(
  params: TimeRangeParams & { groupBy: GroupByDimension },
  options?: { refetchInterval?: number }
): UseQueryResult<ReportSummaryResponse, Error> {
  return useQuery({
    queryKey: ['reports', 'summary', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params.startDate) searchParams.set('startDate', params.startDate);
      if (params.endDate) searchParams.set('endDate', params.endDate);
      if (params.relativeDays) searchParams.set('relativeDays', String(params.relativeDays));
      searchParams.set('groupBy', params.groupBy);

      return get<ReportSummaryResponse>(`/reports/summary?${searchParams.toString()}`);
    },
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * Hook to fetch report timeline data
 */
export function useReportTimeline(
  params: TimeRangeParams & {
    bucket?: 'day' | 'week' | 'month';
    breakdownBy?: 'action' | 'entityType';
  },
  options?: { refetchInterval?: number }
): UseQueryResult<ReportTimelineResponse, Error> {
  return useQuery({
    queryKey: ['reports', 'timeline', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params.startDate) searchParams.set('startDate', params.startDate);
      if (params.endDate) searchParams.set('endDate', params.endDate);
      if (params.relativeDays) searchParams.set('relativeDays', String(params.relativeDays));
      if (params.bucket) searchParams.set('bucket', params.bucket);
      if (params.breakdownBy) searchParams.set('breakdownBy', params.breakdownBy);

      return get<ReportTimelineResponse>(`/reports/timeline?${searchParams.toString()}`);
    },
    staleTime: 60 * 1000,
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * Hook to fetch top actors
 */
export function useReportTopActors(
  params: TimeRangeParams & { limit?: number },
  options?: { refetchInterval?: number }
): UseQueryResult<ReportTopActorsResponse, Error> {
  return useQuery({
    queryKey: ['reports', 'top-actors', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params.startDate) searchParams.set('startDate', params.startDate);
      if (params.endDate) searchParams.set('endDate', params.endDate);
      if (params.relativeDays) searchParams.set('relativeDays', String(params.relativeDays));
      if (params.limit) searchParams.set('limit', String(params.limit));

      return get<ReportTopActorsResponse>(`/reports/top-actors?${searchParams.toString()}`);
    },
    staleTime: 60 * 1000,
    refetchInterval: options?.refetchInterval,
  });
}
